/**
 * 敏感操作安全校验（独立密码 / 短信）。
 *
 * 规则：
 * - 均未开启：直接通过
 * - 仅开启一项：只弹对应一种验证
 * - 两项均开启：先「验证独立密码」，通过后自动再弹「短信验证」
 */

const SESSION_VERIFIED_KEY = 'account_security_verified';
/** 浏览器控制台：localStorage.setItem('debug_security_verify_bypass', '1') 开启；'0' 关闭 */
const DEBUG_BYPASS_STORAGE_KEY = 'debug_security_verify_bypass';

let hostVm = null;

/**
 * 调试模式：跳过独立密码 / 短信弹窗，直接视为校验通过
 * - .env 中 VUE_APP_SECURITY_VERIFY_BYPASS=true（dev 已默认开启）
 * - 或 localStorage debug_security_verify_bypass=1
 */
export function isSecurityVerifyDebugBypass() {
  return false;
  // try {
  //   const flag = localStorage.getItem(DEBUG_BYPASS_STORAGE_KEY);
  //   if (flag === '0') return false;
  //   if (flag === '1') return true;
  // } catch (e) {
  //   /* ignore */
  // }
  // return process.env.VUE_APP_SECURITY_VERIFY_BYPASS === 'true';
}

export function setSecurityVerifyDebugBypass(enabled) {
  try {
    if (enabled) {
      localStorage.setItem(DEBUG_BYPASS_STORAGE_KEY, '1');
    } else {
      localStorage.removeItem(DEBUG_BYPASS_STORAGE_KEY);
    }
  } catch (e) {
    console.warn('[security] 无法设置调试绕过', e);
  }
}

export function registerSecuritySensitiveVerifyHost(vm) {
  hostVm = vm;
}

export function unregisterSecuritySensitiveVerifyHost(vm) {
  if (hostVm === vm) {
    hostVm = null;
  }
}

export function needsAccountSecurityVerify(store) {
  const sec = store.getters.accountSecurity || {};
  return !!(sec.independentPwdOn || sec.smsOn);
}

export function isAccountSecurityVerified() {
  return sessionStorage.getItem(SESSION_VERIFIED_KEY) === '1';
}

export function markAccountSecurityVerified() {
  sessionStorage.setItem(SESSION_VERIFIED_KEY, '1');
}

export function clearAccountSecurityVerified() {
  sessionStorage.removeItem(SESSION_VERIFIED_KEY);
}

/**
 * @param {object} store
 * @param {{ mode?: 'verify'|'login' }} [options]
 *   mode='verify'（默认）弹窗内部独立验证，resolve()
 *   mode='login'  弹窗收集值后 resolve({ sec_pw?, pcode? }) 透传给调用方
 * @returns {Promise<object>}
 */
export function requestSecuritySensitiveVerify(store, options = {}) {
  return new Promise((resolve, reject) => {
    if (isSecurityVerifyDebugBypass()) {
      resolve({});
      return;
    }
    if (!hostVm || typeof hostVm.beginSensitiveVerify !== 'function') {
      reject(new Error('SecuritySensitiveVerifyHost 未挂载'));
      return;
    }
    if (!options.onlySms && !needsAccountSecurityVerify(store)) {
      resolve({});
      return;
    }
    hostVm.beginSensitiveVerify(store, resolve, reject, options);
  });
}

/**
 * @param {object} store Vuex store
 * @param {{ oncePerSession?: boolean }} options oncePerSession 为 true 时登录态内仅校验一次
 */
export async function ensureSecuritySensitiveVerify(store, options = {}) {
  const { oncePerSession = false, onlySms = false } = options;
  if (isSecurityVerifyDebugBypass()) {
    if (oncePerSession) {
      markAccountSecurityVerified();
    }
    return;
  }
  if (!onlySms && !needsAccountSecurityVerify(store)) {
    return;
  }
  if (oncePerSession && isAccountSecurityVerified()) {
    return;
  }
  await requestSecuritySensitiveVerify(store, options);
  if (oncePerSession) {
    markAccountSecurityVerified();
  }
}

export function isSecurityVerifyCancelled(err) {
  return (
    err &&
    (err.message === 'SECURITY_VERIFY_CANCEL' || err.message === 'NO_PHONE')
  );
}

/**
 * 登录场景专用：在没有完整 Vuex store 的情况下弹出安全验证框。
 * safeState 来自 get_safe_state4 接口：{ phone_auth, sec_pw, phone }
 * mode 固定为 'login'，resolve({ sec_pw?, pcode? }) 把值透传给 login_pw 接口。
 *
 * @param {{ sec_pw: number, phone_auth: number, phone?: string }} safeState
 * @returns {Promise<{ sec_pw?: string, pcode?: string }>}
 */
export function requestLoginSecurityVerify(safeState) {
  return new Promise((resolve, reject) => {
    if (isSecurityVerifyDebugBypass()) {
      resolve({});
      return;
    }
    if (!hostVm || typeof hostVm.beginSensitiveVerify !== 'function') {
      reject(new Error('SecuritySensitiveVerifyHost 未挂载'));
      return;
    }

    const independentPwdOn = Number(safeState.sec_pw) === 1;
    const smsOn = Number(safeState.phone_auth) === 1;

    if (!independentPwdOn && !smsOn) {
      resolve({});
      return;
    }

    // 构造一个最小 store-like 对象供 beginSensitiveVerify 读取
    const fakeStore = {
      getters: {
        accountSecurity: { independentPwdOn, smsOn },
      },
      state: {
        userInfo: { uin: safeState.uin || '' },
        developerInfo: { phone: safeState.phone || '' },
      },
    };

    hostVm.beginSensitiveVerify(fakeStore, resolve, reject, { mode: 'login' });
  });
}
